/**
 * CommunicationsAdmin.jsx — COM-01 Unified Omnichannel Administration Workspace
 *
 * Provides a single pane for all channel connections:
 *   Registry | WhatsApp | Email | Facebook | Instagram
 *
 * Key rules (COM-01):
 *  - Mock connections are never displayed as LIVE
 *  - SIMULATED badge shown for mock transports
 *  - WhatsAppChannelSetup and EmailChannelSetup reused as-is
 *  - Facebook and Instagram mount new mock setup pages
 *  - Channel registry aggregates all channel statuses from GET /api/comms/registry
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { hasCapability } from "@/services/api";
import WhatsAppChannelSetup from "./WhatsAppChannelSetup";
import EmailChannelSetup from "./EmailChannelSetup";
import FacebookChannelSetup from "./FacebookChannelSetup";
import InstagramChannelSetup from "./InstagramChannelSetup";
import OmnichannelCatalog from "@/components/OmnichannelCatalog";
import {
  MessageSquare, Mail, Globe, Activity, ChevronRight,
  CheckCircle2, AlertTriangle, XCircle, Wifi, WifiOff,
  RefreshCw, Settings, LayoutGrid
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// ── Channel status helpers ─────────────────────────────────────────────────────

const STATUS_CONFIG = {
  ACTIVE:        { color: "#166534", bg: "#DCFCE7", label: "Active — Live",               dot: "#16A34A" },
  UAT_ACTIVE:    { color: "#7A1A2B", bg: "#FDF2F5", label: "UAT Enabled (Allowlist Gated)", dot: "#C9A15F" },
  VALIDATE:      { color: "#92400E", bg: "#FEF9C3", label: "Validated",                   dot: "#D97706" },
  CONFIGURE:     { color: "#1E40AF", bg: "#DBEAFE", label: "Setup In Progress",           dot: "#3B82F6" },
  UNCONFIGURED:  { color: "#57534E", bg: "#F4F0EA", label: "Not Configured",              dot: "#A8A29E" },
  PAUSED:        { color: "#7C3AED", bg: "#EDE9FE", label: "Paused",                      dot: "#8B5CF6" },
  DISCONNECTED:  { color: "#991B1B", bg: "#FEE2E2", label: "Disconnected",                dot: "#EF4444" },
};

const CHANNEL_META = {
  WHATSAPP:  { label: "WhatsApp",  icon: MessageSquare, color: "#25D366", tabId: "whatsapp" },
  EMAIL:     { label: "Email",     icon: Mail,          color: "#EA4335", tabId: "email"    },
  FACEBOOK:  { label: "Facebook",  icon: Globe,         color: "#1877F2", tabId: "facebook" },
  INSTAGRAM: { label: "Instagram", icon: Activity,      color: "#E1306C", tabId: "instagram"},
};

// ── ChannelCard ────────────────────────────────────────────────────────────────

function ChannelCard({ channel, onManage }) {
  const meta = CHANNEL_META[channel.channel] || {};
  const Icon = meta.icon || Globe;
  const statusCfg = STATUS_CONFIG[channel.status] || STATUS_CONFIG.UNCONFIGURED;

  return (
    <div
      data-testid={`channel-card-${channel.channel.toLowerCase()}`}
      className="flex items-center justify-between p-4 rounded-xl transition-all"
      style={{ background: "#FFFFFF", border: "1px solid var(--brand-border)" }}
    >
      <div className="flex items-center gap-3">
        {/* Channel icon */}
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: `${meta.color}15` }}
        >
          <Icon className="w-5 h-5" style={{ color: meta.color }} />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold" style={{ color: "#1C1917" }}>{meta.label}</span>
            {/* Mock badge */}
            {channel.is_mock && (
              <span
                data-testid={`mock-badge-${channel.channel.toLowerCase()}`}
                className="text-[9px] font-bold px-1.5 py-0.5 rounded-full tracking-wide"
                style={{ background: "#FEF9C3", color: "#92400E", border: "1px solid #FDE047" }}
              >
                MOCK · SIMULATED
              </span>
            )}
            {/* UAT Active badge */}
            {channel.status === "UAT_ACTIVE" && !channel.is_mock && (
              <span
                data-testid={`uat-active-badge-${channel.channel.toLowerCase()}`}
                className="text-[9px] font-bold px-1.5 py-0.5 rounded-full tracking-wide"
                style={{ background: "#FDF2F5", color: "#7A1A2B", border: "1px solid #C9A15F" }}
              >
                UAT · ALLOWLIST
              </span>
            )}
            {/* Live badge */}
            {channel.is_live && channel.status === "ACTIVE" && (
              <span
                data-testid={`live-badge-${channel.channel.toLowerCase()}`}
                className="text-[9px] font-bold px-1.5 py-0.5 rounded-full tracking-wide"
                style={{ background: "#DCFCE7", color: "#166534", border: "1px solid #86EFAC" }}
              >
                LIVE
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <div className="w-1.5 h-1.5 rounded-full" style={{ background: statusCfg.dot }} />
            <span className="text-xs" style={{ color: statusCfg.color }}>{statusCfg.label}</span>
            {channel.phone_number && (
              <span className="text-xs" style={{ color: "#A8A29E" }}>· {channel.phone_number}</span>
            )}
            {channel.from_address && (
              <span className="text-xs" style={{ color: "#A8A29E" }}>· {channel.from_address}</span>
            )}
          </div>
          {channel.uat_notice && (
            <div className="mt-1 text-[10px] rounded px-2 py-0.5"
              style={{ background: "#FDF2F5", color: "#7A1A2B", border: "1px solid #f5c6d0" }}>
              {channel.uat_notice}
            </div>
          )}
        </div>
      </div>
      <button
        data-testid={`manage-channel-${channel.channel.toLowerCase()}`}
        onClick={() => onManage(meta.tabId)}
        className="flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
        style={{ background: "#F4F0EA", color: "#57534E" }}
        onMouseEnter={e => { e.currentTarget.style.background = "#E8E2DA"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "#F4F0EA"; }}
      >
        <Settings className="w-3.5 h-3.5" /> Manage
      </button>
    </div>
  );
}

// ── RegistryTab ───────────────────────────────────────────────────────────────

function RegistryTab({ onNavigateToChannel }) {
  const [registry, setRegistry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadRegistry = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/comms/registry`, { withCredentials: true });
      setRegistry(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load channel registry.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRegistry(); }, [loadRegistry]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: "var(--brand-gold)", borderTopColor: "transparent" }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 rounded-xl" style={{ background: "#FEE2E2" }}>
        <AlertTriangle className="w-4 h-4" style={{ color: "#991B1B" }} />
        <span className="text-sm" style={{ color: "#991B1B" }}>{error}</span>
      </div>
    );
  }

  const summary = registry?.summary || {};

  return (
    <div className="space-y-5" data-testid="channel-registry">
      {/* Summary row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Total Channels",  value: summary.total || 0,        color: "#57534E" },
          { label: "Live",            value: summary.live_count || 0,   color: "#166534" },
          { label: "Mock / Simulated",value: summary.mock_count || 0,   color: "#92400E" },
          { label: "Not Configured",  value: summary.unconfigured || 0, color: "#A8A29E" },
        ].map(({ label, value, color }) => (
          <div key={label} className="p-3 rounded-xl text-center" style={{ background: "#FFFFFF", border: "1px solid var(--brand-border)" }}>
            <p className="text-xl font-bold" style={{ color }}>{value}</p>
            <p className="text-xs mt-0.5" style={{ color: "#78716C" }}>{label}</p>
          </div>
        ))}
      </div>

      {/* Channel cards */}
      <div className="space-y-2">
        {(registry?.channels || []).map(ch => (
          <ChannelCard key={ch.channel} channel={ch} onManage={onNavigateToChannel} />
        ))}
      </div>

      {/* COM-01 mock notice */}
      <div
        className="p-3 rounded-xl text-xs"
        style={{ background: "#FEF9C3", border: "1px solid #FDE047", color: "#92400E" }}
      >
        <strong>COM-01 Notice:</strong> Facebook and Instagram channels are in MOCK mode (DEV/UAT).
        Messages are SIMULATED and never delivered to real recipients.
        WhatsApp and Email can be promoted to LIVE by configuring real provider credentials (COM-02).
      </div>

      <button
        onClick={loadRegistry}
        className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg"
        style={{ background: "#F4F0EA", color: "#57534E" }}
      >
        <RefreshCw className="w-3.5 h-3.5" /> Refresh Registry
      </button>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

const TABS = [
  { id: "registry",  label: "Registry",   icon: Activity,      testId: "comms-tab-registry"  },
  { id: "catalog",   label: "Catalog",     icon: LayoutGrid,    testId: "comms-tab-catalog"   },
  { id: "whatsapp",  label: "WhatsApp",   icon: MessageSquare, testId: "comms-tab-whatsapp"  },
  { id: "email",     label: "Email",      icon: Mail,          testId: "comms-tab-email"     },
  { id: "facebook",  label: "Facebook",   icon: Globe,         testId: "comms-tab-facebook"  },
  { id: "instagram", label: "Instagram",  icon: Activity,      testId: "comms-tab-instagram" },
];

export default function CommunicationsAdmin({ initialTab = "registry" }) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(initialTab);

  const canManage = hasCapability(user, "comms.channel.manage");

  return (
    <div className="space-y-4" data-testid="communications-admin-workspace">
      {/* Section header */}
      <div>
        <h2 className="text-base font-bold" style={{ color: "#1C1917" }}>
          Omnichannel Communications
        </h2>
        <p className="text-xs mt-0.5" style={{ color: "#78716C" }}>
          Configure and monitor all inbound and outbound communication channels for this tenant.
        </p>
      </div>

      {/* Sub-tabs */}
      <div
        className="flex gap-1 p-1 rounded-xl overflow-x-auto"
        style={{ background: "#F4F0EA" }}
        data-testid="comms-channel-tabs"
      >
        {TABS.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              data-testid={tab.testId}
              onClick={() => setActiveTab(tab.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all"
              style={{
                background: isActive ? "#FFFFFF" : "transparent",
                color: isActive ? "#1C1917" : "#78716C",
                boxShadow: isActive ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
              }}
            >
              <Icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div>
        {activeTab === "registry"  && <RegistryTab onNavigateToChannel={setActiveTab} />}
        {activeTab === "catalog"   && <OmnichannelCatalog onNavigateToChannel={setActiveTab} />}
        {activeTab === "whatsapp"  && <WhatsAppChannelSetup />}
        {activeTab === "email"     && <EmailChannelSetup />}
        {activeTab === "facebook"  && <FacebookChannelSetup />}
        {activeTab === "instagram" && <InstagramChannelSetup />}
      </div>
    </div>
  );
}
